import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Teacher } from '../../utils/firebase';
import Card from '../../components/ui/Card';

const PublicTeacherProfile: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTeacherProfile = async () => {
      if (!teacherId) {
        setError('교사 ID가 제공되지 않았습니다.');
        setLoading(false);
        return;
      }

      try {
        const teacherDoc = await getDoc(doc(db, 'teachers', teacherId));
        
        if (teacherDoc.exists()) {
          const teacherData = teacherDoc.data() as Teacher;
          setTeacher(teacherData);
        } else {
          setError('교사 프로필을 찾을 수 없습니다.');
        }
      } catch (err) {
        console.error('교사 프로필 로드 실패:', err);
        setError('교사 프로필을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherProfile();
  }, [teacherId]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #F0F0F0',
          borderTop: '4px solid var(--Color-fill-brand-color, #FF6F0F)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error || !teacher) {
    return (
      <div style={{ 
        maxWidth: '900px', 
        margin: '32px auto',
        padding: '0 16px',
      }}>
        <div style={{
          padding: '16px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: '8px',
          border: '1px solid #ef5350',
        }}>
          {error || '교사 프로필을 찾을 수 없습니다.'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: '900px', 
      margin: '32px auto',
      padding: '0 16px',
    }}>
      <h1 style={{ 
        fontSize: '32px', 
        fontWeight: 400, 
        lineHeight: '48px', 
        letterSpacing: '-0.02em',
        color: 'var(--Color-text-strong, #0D0D0D)',
        margin: 0,
        marginBottom: '16px',
      }}>
        교사 프로필
      </h1>
      
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '24px',
      }}
      className="responsive-flex-row"
      >
        {/* 프로필 카드 */}
        <div style={{ width: '100%' }} className="responsive-width-30">
          <div style={{ 
            padding: '24px', 
            textAlign: 'center',
            backgroundColor: 'var(--Color-background-normal, white)',
            borderRadius: '8px',
            boxShadow: '0px 1px 2px 0 rgba(0, 0, 0, 0.05)',
          }}>
            <div
              style={{ 
                width: '120px', 
                height: '120px', 
                margin: '0 auto', 
                marginBottom: '16px',
                fontSize: '3rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--Color-fill-brand-color, #FF6F0F)',
                color: 'white',
                fontWeight: 'bold',
                overflow: 'hidden',
              }}
            >
              {teacher?.photoURL ? (
                <img 
                  src={teacher.photoURL} 
                  alt={teacher?.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                teacher?.name?.[0] || '?'
              )}
            </div>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: 500, 
              margin: 0,
              marginBottom: '8px',
              color: 'var(--Color-text-strong, #0D0D0D)',
            }}>
              {teacher?.name}
            </h2>
            <p style={{ 
              fontSize: '16px', 
              lineHeight: '24px',
              color: 'var(--Color-text-normal, #161616)',
              margin: 0,
              marginBottom: '8px',
            }}>
              {teacher?.subject} 교사
            </p>
            <div style={{ 
              display: 'inline-block',
              padding: '4px 12px',
              marginTop: '8px',
              border: '1px solid var(--Color-fill-brand-color, #FF6F0F)',
              borderRadius: '16px',
              color: 'var(--Color-fill-brand-color, #FF6F0F)',
              fontSize: '14px',
              fontWeight: 500,
            }}>
              교사
            </div>
          </div>
        </div>

        {/* 상세 정보 */}
        <div style={{ width: '100%' }} className="responsive-width-70">
          <div style={{ 
            padding: '24px',
            backgroundColor: 'var(--Color-background-normal, white)',
            borderRadius: '8px',
            boxShadow: '0px 1px 2px 0 rgba(0, 0, 0, 0.05)',
          }}>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: 600, 
              margin: 0,
              marginBottom: '16px',
              color: 'var(--Color-text-strong, #0D0D0D)',
            }}>
              기본 정보
            </h2>
            <div style={{ 
              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)', 
              marginBottom: '16px',
            }} />
            
            {/* 자기소개 */}
            {teacher?.bio && (
              <div style={{ marginBottom: '24px' }}>
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                  marginBottom: '8px',
                }}>
                  자기소개
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '24px',
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  margin: 0,
                  marginBottom: '16px',
                }}>
                  {teacher.bio}
                </p>
              </div>
            )}
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flexWrap: 'wrap', 
              gap: '16px',
            }} className="responsive-flex-row-sm">
              <div style={{ width: '100%' }} className="responsive-width-45">
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                  marginBottom: '4px',
                }}>
                  담당 과목
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '24px',
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  margin: 0,
                  marginBottom: '16px',
                }}>
                  {teacher?.subject || '정보 없음'}
                </p>
              </div>
              
              <div style={{ width: '100%' }} className="responsive-width-45">
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                  marginBottom: '4px',
                }}>
                  연락처 이메일
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '24px',
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  margin: 0,
                  marginBottom: '16px',
                }}>
                  {teacher?.contactEmail || teacher?.email || '정보 없음'}
                </p>
              </div>
              
              <div style={{ width: '100%' }} className="responsive-width-45">
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                  marginBottom: '4px',
                }}>
                  전화번호
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '24px',
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  margin: 0,
                  marginBottom: '16px',
                }}>
                  {teacher?.phone || '정보 없음'}
                </p>
              </div>
              
              <div style={{ width: '100%' }} className="responsive-width-45">
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                  marginBottom: '4px',
                }}>
                  웹사이트
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '24px',
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  margin: 0,
                  marginBottom: '16px',
                }}>
                  {teacher?.website ? (
                    <a href={teacher.website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--Color-fill-brand-color, #FF6F0F)' }}>
                      {teacher.website}
                    </a>
                  ) : '정보 없음'}
                </p>
              </div>
              
              <div style={{ width: '100%' }} className="responsive-width-45">
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                  marginBottom: '4px',
                }}>
                  위치
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '24px',
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  margin: 0,
                  marginBottom: '16px',
                }}>
                  {teacher?.location || '정보 없음'}
                </p>
              </div>
              
              <div style={{ width: '100%' }} className="responsive-width-45">
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                  marginBottom: '4px',
                }}>
                  학교
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '24px',
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  margin: 0,
                  marginBottom: '16px',
                }}>
                  {teacher?.schoolId || '정보 없음'}
                </p>
              </div>
              
              <div style={{ width: '100%' }} className="responsive-width-45">
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                  marginBottom: '4px',
                }}>
                  담임 반
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '24px',
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  margin: 0,
                  marginBottom: '16px',
                }}>
                  {typeof teacher?.homeroomClass === 'string' ? teacher.homeroomClass : '담임 아님'}
                </p>
              </div>
              
              <div style={{ width: '100%' }} className="responsive-width-45">
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                  marginBottom: '4px',
                }}>
                  가입일
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '24px',
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  margin: 0,
                  marginBottom: '16px',
                }}>
                  {teacher?.createdAt ? 
                    (typeof teacher.createdAt === 'object' && 'seconds' in teacher.createdAt ? 
                      new Date((teacher.createdAt as any).seconds * 1000).toLocaleDateString('ko-KR') :
                      new Date(teacher.createdAt).toLocaleDateString('ko-KR')
                    ) : '정보 없음'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 통계 카드들 */}
      <div style={{ marginTop: '24px' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '16px',
        }} className="responsive-flex-row-sm">
          <div style={{ width: '100%' }} className="responsive-width-33">
            <Card padding="24px" borderRadius="8px">
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ 
                  fontSize: '32px', 
                  fontWeight: 600,
                  color: 'var(--Color-fill-brand-color, #FF6F0F)',
                  margin: 0,
                  marginBottom: '8px',
                }}>
                  {teacher?.stampClasses?.length || 0}
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                }}>
                  담당 교과
                </p>
              </div>
            </Card>
          </div>
          
          <div style={{ width: '100%' }} className="responsive-width-33">
            <Card padding="24px" borderRadius="8px">
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ 
                  fontSize: '32px', 
                  fontWeight: 600,
                  color: '#9c27b0',
                  margin: 0,
                  marginBottom: '8px',
                }}>
                  {teacher?.classRoles?.length || 0}
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                }}>
                  관리 중인 역할
                </p>
              </div>
            </Card>
          </div>
          
          <div style={{ width: '100%' }} className="responsive-width-33">
            <Card padding="24px" borderRadius="8px">
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ 
                  fontSize: '32px', 
                  fontWeight: 600,
                  color: '#4caf50',
                  margin: 0,
                  marginBottom: '8px',
                }}>
                  {teacher?.loginType || 'local'}
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)',
                  margin: 0,
                }}>
                  로그인 방식
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* 학력 정보 */}
      {teacher?.education && teacher.education.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <div style={{ 
            padding: '24px',
            backgroundColor: 'var(--Color-background-normal, white)',
            borderRadius: '8px',
            boxShadow: '0px 1px 2px 0 rgba(0, 0, 0, 0.05)',
          }}>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: 600, 
              margin: 0,
              marginBottom: '16px',
              color: 'var(--Color-text-strong, #0D0D0D)',
            }}>
              학력 정보
            </h2>
            <div style={{ 
              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)', 
              marginBottom: '16px',
            }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {teacher.education.map((edu, index) => (
                <div key={edu.id} style={{ 
                  padding: '16px', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '4px',
                  backgroundColor: 'var(--Color-background-normal, white)',
                }}>
                  <h3 style={{ 
                    fontSize: '20px', 
                    fontWeight: 600, 
                    margin: 0,
                    marginBottom: '8px',
                    color: 'var(--Color-text-strong, #0D0D0D)',
                  }}>
                    {edu.degree} - {edu.major}
                  </h3>
                  <p style={{ 
                    fontSize: '16px', 
                    lineHeight: '24px',
                    color: 'var(--Color-text-normal, #161616)',
                    margin: 0,
                  }}>
                    {edu.school} ({edu.graduationYear}년 {edu.isGraduated ? '졸업' : '재학'})
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 강의 이력 */}
      {teacher?.teachingHistory && teacher.teachingHistory.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <div style={{ 
            padding: '24px',
            backgroundColor: 'var(--Color-background-normal, white)',
            borderRadius: '8px',
            boxShadow: '0px 1px 2px 0 rgba(0, 0, 0, 0.05)',
          }}>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: 600, 
              margin: 0,
              marginBottom: '16px',
              color: 'var(--Color-text-strong, #0D0D0D)',
            }}>
              강의 이력
            </h2>
            <div style={{ 
              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)', 
              marginBottom: '16px',
            }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {teacher.teachingHistory.map((history, index) => (
                <div key={history.id} style={{ 
                  padding: '16px', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '4px',
                  backgroundColor: 'var(--Color-background-normal, white)',
                }}>
                  <h3 style={{ 
                    fontSize: '20px', 
                    fontWeight: 600, 
                    margin: 0,
                    marginBottom: '8px',
                    color: 'var(--Color-text-strong, #0D0D0D)',
                  }}>
                    {history.subject} - {history.grade}학년 {history.class}반
                  </h3>
                  <p style={{ 
                    fontSize: '16px', 
                    lineHeight: '24px',
                    color: 'var(--Color-text-normal, #161616)',
                    margin: 0,
                    marginBottom: '8px',
                  }}>
                    {history.school}
                  </p>
                  <p style={{ 
                    fontSize: '14px', 
                    lineHeight: '21px',
                    color: 'var(--Color-text-normal, #161616)',
                    margin: 0,
                  }}>
                    {history.startDate} ~ {history.isCurrent ? '현재' : history.endDate}
                  </p>
                  {history.description && (
                    <p style={{ 
                      fontSize: '14px', 
                      lineHeight: '21px',
                      margin: 0,
                      marginTop: '8px',
                      fontStyle: 'italic',
                      color: 'var(--Color-text-normal, #161616)',
                    }}>
                      {history.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicTeacherProfile;
